import React, { ReactNode, useEffect } from "react";
import { Button, IButtonProps } from "#components/primitive";
import Icon from "#components/icon";
import { Modal } from "antd";
import classNames from "classnames";
import { useDisclosure } from "#resources/hooks";
import {
	GTMEventSelectContentData,
	SendGTMEventParams,
	useGTMEvent,
} from "#resources/hooks/metrics";
import { extractTextFromReactNode } from "./helpers";

import s from "./modal-onboarding.module.scss";

const DEFAULT_WIDTH = 624;

type Action = {
	label: string | ReactNode;
	onClick?: () => void;
	buttonProps?: Partial<Omit<IButtonProps, "children" | "onClick">>;
	gtmEvent?: Partial<
		Pick<SendGTMEventParams, "event_track" | "extra_data_json"> &
			GTMEventSelectContentData
	>;
};

export type ModalOnboardingProps = {
	onClose?: () => void;
	/**
	 * Default value: 624
	 */
	width?: number;
	title: string | ReactNode;
	content: string | ReactNode;
	className?: string;
	actions: {
		primary: Action;
		secondary?: Action;
	};
	bannerSrc: string;
};

export const ModalOnboarding = ({
	onClose: _onClose,
	width = DEFAULT_WIDTH,
	title,
	content,
	actions,
	className,
	bannerSrc,
}: ModalOnboardingProps) => {
	const { sendSelectContent, sendGTMEvent } = useGTMEvent();

	const extra_data_json = {
		notificationTitle: extractTextFromReactNode(title),
	};

	const { isOpen, onClose } = useDisclosure({
		initialValue: true,
		onCloseCallback: _onClose,
	});

	useEffect(() => {
		if (isOpen) {
			sendGTMEvent({
				event: "view_content",
				event_track: "view_notification",
				extra_data_json,
			});
		}
	}, [isOpen]);

	return (
		<Modal
			footer={null}
			width={width}
			onCancel={() => {
				sendSelectContent({
					event_track: "notification_close",
					label: "X",
					extra_data_json,
				});
				onClose();
			}}
			className={classNames(s.modal, className)}
			open={isOpen}
		>
			<div className={s.wrapper}>
				{bannerSrc && <img className={s.banner} src={bannerSrc} alt="Banner" />}

				<div className={s.content}>
					<h2>{title}</h2>

					<p>{content}</p>

					<footer>
						{actions.secondary && (
							<Button
								variant="secondary"
								onClick={() => {
									actions.secondary?.onClick?.();

									if (actions.secondary?.label) {
										sendSelectContent({
											event_track: "notification_cta",
											label: actions.secondary?.gtmEvent?.label
												? String(actions.secondary?.gtmEvent?.label)
												: String(actions.secondary.label),
											extra_data_json,
											...actions.secondary?.gtmEvent,
										});
									}

									onClose();
								}}
								{...actions.secondary.buttonProps}
							>
								{actions.secondary.label}
							</Button>
						)}

						<Button
							trailingIcon={<Icon icon="chevron-right" />}
							onClick={() => {
								actions.primary.onClick?.();

								sendSelectContent({
									event_track: "notification_cta",
									label: actions.primary?.gtmEvent?.label
										? String(actions.primary?.gtmEvent?.label)
										: String(actions.primary.label),
									extra_data_json,
									...actions.primary?.gtmEvent,
								});

								onClose();
							}}
							{...actions.primary.buttonProps}
						>
							{actions.primary.label}
						</Button>
					</footer>
				</div>
			</div>
		</Modal>
	);
};
